// src/components/common/table/StandardTableToolbar.tsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';

const clampInt = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Math.trunc(n)));

export type StandardTableToolbarProps = {
  page: number;
  pageSize: number;
  totalCount: number;
  onChangePage: (page: number) => void;

  showTotalCount?: boolean; // default true
  showPageJump?: boolean; // default true

  totalLabel?: string; // default "Total"
  pageLabel?: string; // default "Page"
};

export const StandardTableToolbar: React.FC<StandardTableToolbarProps> = ({
  page,
  pageSize,
  totalCount,
  onChangePage,

  showTotalCount = true,
  showPageJump = true,

  totalLabel = 'Total',
  pageLabel = 'Page',
}) => {
  const safePageSize = Math.max(1, pageSize);
  const totalPages = Math.max(1, Math.ceil((totalCount ?? 0) / safePageSize));

  const [pageInput, setPageInput] = useState(() => String(page));
  useEffect(() => setPageInput(String(page)), [page]);

  const formattedTotal = useMemo(
    () => Number(totalCount ?? 0).toLocaleString(),
    [totalCount],
  );

  const submitPage = useCallback(() => {
    const n = Number(pageInput);
    if (!Number.isFinite(n)) return;

    const next = clampInt(n, 1, totalPages);
    if (next === page) return;

    onChangePage(next);
  }, [onChangePage, page, pageInput, totalPages]);

  if (!showTotalCount && !showPageJump) return null;

  return (
    <Box mb={1}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        {/* Left: Total */}
        {showTotalCount ? (
          <Typography variant="body2" color="text.secondary">
            {totalLabel}: <b>{formattedTotal}</b>
          </Typography>
        ) : (
          <Box />
        )}

        {/* Right: Page jump */}
        {showPageJump ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {pageLabel}
            </Typography>

            <TextField
              size="small"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value.replace(/[^\d]/g, ''))}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                submitPage();
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: { textAlign: 'center', width: 56 },
                'aria-label': 'Go to page',
              }}
            />

            <Typography variant="body2" color="text.secondary">
              / {totalPages}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              onClick={submitPage}
              disabled={totalPages <= 1}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Go
            </Button>
          </Stack>
        ) : (
          <Box />
        )}
      </Stack>
    </Box>
  );
};
